import {intRangeClosed} from "../../../bootstrap/common/arrays";
import {GLOBAL} from "../../../common/globals";
import {resolve} from "../../../container";
import {html, type PropertyValues, type TemplateResult} from "lit";
import {customElement, state} from "lit/decorators.js";
import {EventBus} from "../../../common/eventBus";
import type {FootnoteElement} from "./footnoteText";
import {textBetween} from "../../../common/utils/between";
import {truncateWordsAfter} from "../../../common/utils/truncate";
import {elementFrom} from "../../../common/utils/html";
import {generateUuid} from "../../../common/crypto";
import {handleActivationKey} from "../../../common/utils/events";
import {Dictionary} from "../dictionary";
import {ClickTracking} from "../../../tracking/clickTracking";
import {UnLitElement} from "../../../common/elements";


export const FOOTNOTE_REFERENCE_CLICK_EVENT = "footnoteClicked";

const MAX_LENGTH_BEFORE_TRUNCATE = 68;

export class Footnote {

    public readonly linkElements: HTMLElement[];
    public readonly id: string;
    public truncatedText: string;

    public constructor(
        public readonly text: string,
        public readonly number: number,
        private eventBus: EventBus = resolve(EventBus),
        private clickTracking: ClickTracking = resolve(ClickTracking)
    ) {
        this.linkElements = [];
        this.id = generateUuid();
        const element = elementFrom<HTMLElement>(`<span>${this.text}</span>`);
        truncateWordsAfter(element, MAX_LENGTH_BEFORE_TRUNCATE);
        this.truncatedText = element.innerHTML;
    }

    public addLinkElement(numberElement: HTMLElement): void {
        numberElement.textContent = "" + this.number;
        numberElement.classList.add("footnote-loaded");
        numberElement.setAttribute("tabindex", "0");
        numberElement.setAttribute("role", "link");
        numberElement.setAttribute("aria-label", this.footnoteLabel(numberElement));
        numberElement.addEventListener("click", event => this.dispatchFootnoteEvent(event, numberElement));
        numberElement.addEventListener("keydown", (event: KeyboardEvent) => {
            handleActivationKey(event, () => this.dispatchFootnoteEvent(event, numberElement));
        });
        this.linkElements.push(numberElement);
    }

    private dispatchFootnoteEvent(event: Event, numberElement: HTMLElement): void {
        this.clickTracking.handleTextlinkInteract(event, this.footnoteLabel(numberElement));
        this.eventBus.dispatchEvent(FOOTNOTE_REFERENCE_CLICK_EVENT, {id: this.id});
    }

    private footnoteLabel(numberElement: HTMLElement): string {
        const dictionary = Dictionary.of(numberElement);
        return `${dictionary.translate("MSG_FOOTNOTE")} ${this.number}`;
    }
}

export class FootnoteReferenceElements {

    public constructor(public readonly elements: HTMLElement[]) {
    }

    public collect(): Footnote[] {
        const footnotes: Footnote[] = [];

        for (const element of this.elements) {
            const footnoteText = element.querySelector(".footnote-text-hidden")!.innerHTML ?? "";
            const linkElement = element.querySelector<HTMLElement>(".footnote-reference")!;

            const existingFootnote = footnotes.find(footnote => footnote.text === footnoteText);
            if (existingFootnote) {
                existingFootnote.addLinkElement(linkElement);
            } else {
                const newFootnote = new Footnote(footnoteText, footnotes.length + 1);
                newFootnote.addLinkElement(linkElement);
                footnotes.push(newFootnote);
            }
        }

        return footnotes;
    }

    public separateNeighboursBy(separator: HTMLElement): void {
        for (let i = 0; i < this.elements.length - 1; i++) {
            if (this.elements[i].nextElementSibling === this.elements[i + 1] && this.noTextBetween(this.elements[i], this.elements[i + 1])) {
                this.elements[i].after(separator.cloneNode(true));
            }
        }
    }

    private noTextBetween(element1: HTMLElement, element2: HTMLElement): boolean {
        return textBetween(element1, element2).trim() === "";
    }
}

interface CrawlerWaitingForTargetOutput {
    forTargetFootnoteOutput: (output: HTMLElement) => ReadyCrawler;
}

interface ReadyCrawler {
    findFootnoteReferences: () => FootnoteReferenceElements;
}

export class FootnoteCrawler implements CrawlerWaitingForTargetOutput, ReadyCrawler {
    private targetFootnoteOutput: HTMLElement;

    private constructor() {
    }

    public static newInstance(): CrawlerWaitingForTargetOutput {
        return new FootnoteCrawler();
    }

    public forTargetFootnoteOutput(output: HTMLElement): ReadyCrawler {
        this.targetFootnoteOutput = output;
        return this;
    }

    public findFootnoteReferences(): FootnoteReferenceElements {
        const elementsOfConcern = Array.from(GLOBAL.bodyElement().querySelectorAll<HTMLElement>("eop-footnotes, .footnote"));

        const targetOutputIndex = elementsOfConcern.indexOf(this.targetFootnoteOutput);
        const previousOutputIndex = this.findLastFootnoteOutputIn(elementsOfConcern, targetOutputIndex - 1);

        const elements: HTMLElement[] = intRangeClosed(previousOutputIndex + 1, targetOutputIndex - 1).map(i => elementsOfConcern[i]);
        return new FootnoteReferenceElements(elements);
    }

    private findLastFootnoteOutputIn(elements: HTMLElement[], maxIndex: number): number {
        for (let i = maxIndex; i >= 0; i--) {
            if (elements[i] instanceof EopFootnotes) {
                return i;
            }
        }
        return -1;
    }
}

@customElement("eop-footnotes")
export class EopFootnotes extends UnLitElement {

    @state()
    private footnotes: Footnote[] = [];

    protected firstUpdated(_changedProperties: PropertyValues): void {
        super.firstUpdated(_changedProperties);

        const footnoteReferenceElements = FootnoteCrawler.newInstance()
            .forTargetFootnoteOutput(this)
            .findFootnoteReferences();
        footnoteReferenceElements.separateNeighboursBy(elementFrom("<sup>,</sup>"));
        this.footnotes = footnoteReferenceElements.collect();
    }

    public render(): TemplateResult {
        return html`
            <ul class="footnotes">
                ${(this.renderFootnotes())}
            </ul>
        `;
    }

    private renderFootnotes(): TemplateResult[] {
        return this.footnotes.map((footnote, index) => html`
            <li id=${`li-${footnote.id}`}>
                <eop-footnote-text id=${footnote.id} .footnote=${this.mapFootnote(footnote, index + 1)}></eop-footnote-text>
            </li>
        `);
    }

    private mapFootnote(footnote: Footnote, index: number): FootnoteElement {
        return {
            text: footnote.text,
            number: index,
            truncatedText: footnote.truncatedText
        };
    }
}